import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar/navbar";
import Footer from "../Footer/footer";
import FooterDoodles from "../Footer/footerDoodles";
import icons from "../../icons.config";
import { forgotPasswordApi, verifyForgotOtpApi } from "../../Providers/authProvider";

const ForgotPassword = () => {
  const [email, setEmail] = useState(""); // Changed from phone to email
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Email validation regex
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Timer effect
  useEffect(() => {
    if (otpSent && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [otpSent, timeLeft]);

  // Send OTP API Call with Enhanced Validation
  const handleSendOtp = async () => {
    // Check if email is empty
    if (!email) {
      setError("Email cannot be empty.");
      return;
    }

    // Validate email format
    if (!validateEmail(email)) {
      setError("Enter a valid email address.");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await forgotPasswordApi(email); // Pass email instead of phone
      setOtpSent(true);
      setMessage(response.message || `OTP sent to ${email}`);
      setTimeLeft(300); // Set timer to 5 minutes (300 seconds)
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP API Call
  const handleVerifyOtp = async () => {
    if (!email) {
      setError("Email cannot be empty.");
      return;
    }

    if (!validateEmail(email)) {
      setError("Enter a valid email address.");
      return;
    }

    if (!otp) {
      setError("Please enter the OTP.");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await verifyForgotOtpApi(email, otp); // Pass email instead of phone
      setMessage(response.message || "OTP Verified Successfully.");
      navigate("/reset-password"); // Redirect to Reset Password page
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="relative min-h-screen flex items-center justify-center bg-tertiary top-10 -mb-52  overflow-hidden">
        <div className="absolute -right-52 -top-12 lg:-right-32 lg:-top-10 z-40">
          <img src={icons.trendCircles} alt="Doodle Right" className="w-[50%] lg:w-[70%]" />
        </div>
        <div className="hidden lg:block absolute left-8 top-20 z-10">
          <img src={icons.aboutUsFlower} alt="Doodle Left" className="w-[77%]" />
        </div>

        {/* Forgot Password Box */}
        <div className="relative z-10 w-full max-w-md bg-tertiary px-8">
          <h2 className="text-center text-3xl font-outfit text-primary1 font-light mb-8">FORGOT PASSWORD</h2>
          {error && <p className="text-hcolor font-poppins font-light text-center mb-1">{error}</p>}
          {message && <p className="text-green-500 font-poppins font-light text-center mb-1">{message}</p>}

          {/* Email Input */}
          <div className="mb-0">
            <label htmlFor="email" className="relative left-3 px-2 bg-tertiary text-primary1 text-md font-medium transform -translate-y-4 scale-75 top-3">
              EMAIL
            </label>
            <input
              type="email" // Changed to type="email"
              value={email}
              onChange={(e) => {
                const input = e.target.value;
                setEmail(input);
              }}
              placeholder="Enter your email address"
              className="w-full px-4 py-3 border rounded-md bg-tertiary text-primary1 text-sm focus:outline-none focus:ring-2 focus:ring-primary1"
              disabled={otpSent}
            />

            {/* Send OTP Button */}
            <div className="flex justify-end mt-2 font-poppins font-light">
              <button
                onClick={handleSendOtp}
                className={`text-sm underline ${otpSent ? 'text-secondary' : 'text-hcolor'}`}
                disabled={otpSent || loading}
              >
                {loading ? "Sending..." : "Send OTP"}
              </button>
            </div>
          </div>

          {/* OTP Input */}
          <div className="mb-8">
            <label htmlFor="otp" className="relative left-3 px-2 bg-tertiary text-primary1 text-md font-medium transform -translate-y-4 scale-75 top-3">
              OTP
            </label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter your OTP"
              className="w-full px-4 py-3 border rounded-md bg-tertiary text-primary1 text-sm focus:outline-none focus:ring-2 focus:ring-primary1"
              disabled={!otpSent}
            />
          </div>

          {/* Verify OTP Button */}
          <button
            onClick={handleVerifyOtp}
            className="w-full bg-hcolor text-tertiary px-6 py-3 text-sm font-normal hover:bg-tertiary hover:border-2 hover:text-hcolor focus:outline-none focus:ring-2 focus:ring-primary1"
            disabled={loading}
          >
            {loading ? "Verifying..." : "VERIFY OTP"}
          </button>

          {/* Resend OTP & Timer */}
          <div className="text-center mt-6 space-y-4">
            <p className="text-sm text-hcolor">
              {timeLeft > 0 ? (
                `Resend OTP in ${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, "0")}`
              ) : (
                <span className="text-primary1 cursor-pointer underline font-light" onClick={handleSendOtp}>
                  Resend OTP
                </span>
              )}
            </p>
            <p className="text-sm text-hcolor">
              Having trouble? <a href="/contact" className="text-primary1 underline">Get Help</a>
            </p>
          </div>
        </div>
      </div>
      <FooterDoodles />
      <Footer />
    </>
  );
};

export default ForgotPassword;












// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import Navbar from "../Navbar/navbar";
// import Footer from "../Footer/footer";
// import FooterDoodles from "../Footer/footerDoodles";
// import icons from "../../icons.config";
// import { forgotPasswordApi,verifyForgotOtpApi } from "../../Providers/authProvider";

// const ForgotPassword = () => {
//   const [phone, setPhone] = useState("");
//   const [otp, setOtp] = useState("");
//   const [otpSent, setOtpSent] = useState(false);
//   const [error, setError] = useState("");
//   const [message, setMessage] = useState("");
//   const [timeLeft, setTimeLeft] = useState(0);
//   const [loading, setLoading] = useState(false);
//   const navigate = useNavigate();

//   // Timer effect
//   useEffect(() => {
//     if (otpSent && timeLeft > 0) {
//       const timer = setInterval(() => {
//         setTimeLeft((prev) => prev - 1);
//       }, 1000);
//       return () => clearInterval(timer);
//     }
//   }, [otpSent, timeLeft]);

//   // Send OTP API Call
// // Send OTP API Call with Enhanced Validation
// const handleSendOtp = async () => {
//   // Check if phone number is empty
//   if (!phone) {
//     setError("Phone number cannot be empty.");
//     return;
//   }

//   // Validate phone number length and format
//   if (phone.length !== 10 || !/^\d{10}$/.test(phone)) {
//     setError("Enter a valid 10-digit phone number.");
//     return;
//   }

//   setLoading(true);
//   setError(""); 
//   setMessage("");

//   try {
//     const response = await forgotPasswordApi(phone);
//     setOtpSent(true);
//     setMessage(response.message || `OTP sent to ${phone}`);
//     setTimeLeft(300); // Set timer to 5 minutes (300 seconds)
//   } catch (error) {
//     setError(error.message);
//   } finally {
//     setLoading(false);
//   }
// };


//   // Verify OTP API Call
//   const handleVerifyOtp = async () => {
//     if (!otp) {
//       setError("Please enter the OTP.");
//       return;
//     }

//     setLoading(true);
//     setError("");
//     setMessage("");

//     try {
//       const response = await verifyForgotOtpApi(phone, otp);
//       setMessage(response.message || "OTP Verified Successfully.");
//       navigate("/reset-password"); // Redirect to Reset Password page
//     } catch (error) {
//       setError(error.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <>
//       <Navbar />
//       <div className="relative min-h-screen flex items-center justify-center bg-tertiary top-10 overflow-hidden">
//         <div className="absolute -right-52 -top-12 lg:-right-32 lg:-top-10 z-40">
//           <img src={icons.trendCircles} alt="Doodle Right" className="w-[50%] lg:w-[70%]" />
//         </div>
//         <div className="hidden lg:block absolute left-8 top-20 z-10">
//           <img src={icons.aboutUsFlower} alt="Doodle Left" className="w-[77%]" />
//         </div>

//         {/* Forgot Password Box */}
//         <div className="relative z-10 w-full max-w-md bg-tertiary px-8">
//           <h2 className="text-center text-3xl font-outfit text-primary1 font-light mb-8">FORGOT PASSWORD</h2>
//           {error && <p className="text-hcolor font-poppins font-light text-center mb-1">{error}</p>}
//           {message && <p className="text-green-500 font-poppins font-light text-center mb-1">{message}</p>}

//           {/* Phone Input */}
//           <div className="mb-0">
//             <label htmlFor="phone" className="relative left-3 px-2 bg-tertiary text-primary1 text-md font-medium transform -translate-y-4 scale-75 top-3">
//               PHONE
//             </label>
//             <input
//               type="tel"
//               value={phone}
//               onChange={(e) => {
//                 const input = e.target.value.replace(/\D/g, "");
//                 if (input.length <= 10) setPhone(input);
//               }}
//               placeholder="Enter your phone number"
//               maxLength={10}
//               className="w-full px-4 py-3 border rounded-md bg-tertiary text-primary1 text-sm focus:outline-none focus:ring-2 focus:ring-primary1"
//               disabled={otpSent}
//             />

//             {/* Send OTP Button */}
//             <div className="flex justify-end mt-2 font-poppins font-light">
//               <button 
//                 onClick={handleSendOtp} 
//                 className={`text-sm underline ${otpSent ? 'text-secondary' : 'text-hcolor'}`}
//                 disabled={otpSent || loading} 
//               >
//                 {loading ? "Sending..." : "Send OTP"}
//               </button>
//             </div>
//           </div>

//           {/* OTP Input */}
//           <div className="mb-8">
//             <label htmlFor="otp" className="relative left-3 px-2 bg-tertiary text-primary1 text-md font-medium transform -translate-y-4 scale-75 top-3">
//               OTP
//             </label>
//             <input
//               type="text"
//               value={otp}
//               onChange={(e) => setOtp(e.target.value)}
//               placeholder="Enter your OTP"
//               className="w-full px-4 py-3 border rounded-md bg-tertiary text-primary1 text-sm focus:outline-none focus:ring-2 focus:ring-primary1"
//               disabled={!otpSent}
//             />
//           </div>

//           {/* Verify OTP Button */}
//           <button
//             onClick={handleVerifyOtp}
//             className="w-full bg-hcolor text-tertiary px-6 py-3 text-sm font-normal hover:bg-tertiary hover:border-2 hover:text-hcolor focus:outline-none focus:ring-2 focus:ring-primary1"
//             disabled={loading}
//           >
//             {loading ? "Verifying..." : "VERIFY OTP"}
//           </button>

//           {/* Resend OTP & Timer */}
//           <div className="text-center mt-6 space-y-4">
//             <p className="text-sm text-hcolor">
//               {timeLeft > 0 ? `Resend OTP in ${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, "0")}` : (
//                 <span className="text-primary1 cursor-pointer underline font-light" onClick={handleSendOtp}>
//                   Resend OTP
//                 </span>
//               )}
//             </p>
//             <p className="text-sm text-hcolor">
//               Having trouble? <a href="/help" className="text-primary1 underline">Get Help</a>
//             </p>
//           </div>
//         </div>
//       </div>
//       <FooterDoodles />
//       <Footer />
//     </>
//   );
// };

// export default ForgotPassword;
